import React, { useEffect, useRef, useState } from 'react';
import AdminHeader from '../components/AdminHeader';
import { useSelector } from 'react-redux';
import request from '../utils/request';
import {
  FiMessageSquare, FiSend, FiUser, FiMapPin, FiClock,
  FiChevronLeft, FiRefreshCw
} from 'react-icons/fi';

const SupportChat = () => {
    const [threads, setThreads] = useState([]);
    const [selectedThread, setSelectedThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const { adminInfo } = useSelector((state) => state.login);
    const scroll = useRef();

    // Fetch all active driver threads
    const fetchThreads = async () => {
        try {
            setLoading(true);
            // Use request utility which handles auth and base URL
            const res = await request.get('/driver-chat/threads');
            if (res.success) {
                setThreads(res.threads);
            }
        } catch (error) {
            console.error('Error fetching threads:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch messages for a specific thread
    const fetchMessages = async (threadId) => {
        try {
            const res = await request.get(`/driver-chat/threads/${threadId}/messages`);
            if (res.success) {
                setMessages(res.messages);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    // Send message to driver
    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedThread) return;

        try {
            setSending(true);
            await request.post('/driver-chat/message', {
                orderId: selectedThread.orderId,
                driverId: selectedThread.driverId,
                text: newMessage.trim()
            });

            setNewMessage('');
            // Refresh messages
            await fetchMessages(selectedThread.id);
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        fetchThreads();
    }, []);

    useEffect(() => {
        if (scroll?.current) {
            scroll.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    useEffect(() => {
        if (selectedThread) {
            fetchMessages(selectedThread.id);
        }
    }, [selectedThread]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            <AdminHeader />
            <div className="flex flex-1 overflow-hidden">
                {/* Driver Threads List */}
                <div className="w-1/3 bg-white border-r border-gray-200 overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-800">Concierge Chats</h2>
                        <button
                            onClick={fetchThreads}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            title="Refresh threads"
                        >
                            <FiRefreshCw className="w-4 h-4 text-gray-600" />
                        </button>
                    </div>
                    <div className="p-2">
                        {loading ? (
                            <div className="text-center py-8">
                                <FiRefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                                <p className="text-sm text-gray-500 mt-2">Loading threads...</p>
                            </div>
                        ) : threads.length === 0 ? (
                            <div className="text-center py-8">
                                <FiMessageSquare className="w-10 h-10 mx-auto text-gray-400" />
                                <p className="text-sm text-gray-500 mt-2">No active concierge threads</p>
                            </div>
                        ) : (
                            threads.map((thread) => (
                                <div
                                    key={thread.id}
                                    className={`p-3 mb-2 rounded-lg cursor-pointer transition-colors border ${
                                        selectedThread?.id === thread.id
                                            ? 'bg-blue-50 border-blue-300'
                                            : 'bg-white border-gray-200 hover:bg-gray-50'
                                    }`}
                                    onClick={() => setSelectedThread(thread)}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                            {thread.driverProfileImage ? (
                                                <img
                                                    src={thread.driverProfileImage}
                                                    alt="Driver"
                                                    className="w-full h-full rounded-full object-cover"
                                                />
                                            ) : (
                                                <FiUser className="w-5 h-5 text-gray-600" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {thread.driverName || 'Unknown Driver'}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">
                                                Order #{thread.orderId}
                                            </p>
                                            <p className="text-xs text-gray-400 truncate">
                                                {thread.lastMessageText || 'No messages yet'}
                                            </p>
                                        </div>
                                        {thread.unreadCount > 0 && (
                                            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                                                <span className="text-xs text-white font-semibold">
                                                    {thread.unreadCount}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                                        <span>{thread.driverCity}</span>
                                        <span>{new Date(thread.lastMessageAt).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 flex flex-col">
                    {selectedThread ? (
                        <>
                            {/* Chat Header */}
                            <div className="bg-white border-b border-gray-200 p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <button
                                            onClick={() => setSelectedThread(null)}
                                            className="p-2 hover:bg-gray-100 rounded-lg"
                                        >
                                            <FiChevronLeft className="w-4 h-4" />
                                        </button>
                                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                            {selectedThread.driverProfileImage ? (
                                                <img
                                                    src={selectedThread.driverProfileImage}
                                                    alt="Driver"
                                                    className="w-full h-full rounded-full object-cover"
                                                />
                                            ) : (
                                                <FiUser className="w-5 h-5 text-gray-600" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {selectedThread.driverName || 'Unknown Driver'}
                                            </h3>
                                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                                                <span>Order #{selectedThread.orderId}</span>
                                                {selectedThread.driverCity && (
                                                    <span className="flex items-center">
                                                        <FiMapPin className="w-3 h-3 mr-1" />
                                                        {selectedThread.driverCity}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {messages.length === 0 ? (
                                    <div className="text-center py-8">
                                        <FiMessageSquare className="w-10 h-10 mx-auto text-gray-400" />
                                        <p className="text-sm text-gray-500 mt-2">No messages in this thread yet</p>
                                    </div>
                                ) : (
                                    messages.map((message) => (
                                        <div
                                            key={message.id}
                                            className={`flex ${message.isFromDriver ? 'justify-start' : 'justify-end'}`}
                                        >
                                            <div
                                                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                                    message.isFromDriver
                                                        ? 'bg-gray-200 text-gray-800'
                                                        : 'bg-blue-500 text-white'
                                                }`}
                                            >
                                                <p className="text-sm">{message.text}</p>
                                                <p className="text-xs mt-1 opacity-70">
                                                    {new Date(message.timestamp).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div ref={scroll} />
                            </div>

                            {/* Message Input */}
                            <div className="bg-white border-t border-gray-200 p-4">
                                <div className="flex space-x-2">
                                    <input
                                        type="text"
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Type a message to driver..."
                                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={sending}
                                    />
                                    <button
                                        onClick={sendMessage}
                                        disabled={sending || !newMessage.trim()}
                                        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                                    >
                                        {sending ? (
                                            <FiRefreshCw className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <FiSend className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center">
                                <FiMessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No thread selected</h3>
                                <p className="mt-1 text-sm text-gray-500">Choose a concierge thread from the list to view messages and reply.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupportChat;
